import React, {
  useState,
  useMemo,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardBody,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Button,
  CardTitle,
} from "reactstrap";
import { useGetReportTable } from "api/reports/service";
import { getActiveColorStyle } from "utils/colorUtils";
import { useUpdateReport } from "api/reports/service";
import { MdOutlineDragIndicator } from "react-icons/md";
import { getReportSurfaceColors } from "./reportDetailTheme";

const ReportColumns = forwardRef(
  ({ activeColor = "blue", isTheme2 = false, id, onColumnsChange, data, permissions = [] }, ref) => {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedColumns, setSelectedColumns] = useState([]);
    const [expandedTables, setExpandedTables] = useState([]);
    const [activeFilter, setActiveFilter] = useState("all");
    const [isSaving, setIsSaving] = useState(false);
    const [closeSourceArea, setCloseSourceArea] = useState(false);
    const [currentTheme, setCurrentTheme] = useState(() => {
      const darkMode = localStorage.getItem("darkMode");
      return darkMode ? JSON.parse(darkMode) : true;
    });
    const [isLightTheme, setIsLightTheme] = useState(false);
    const [draggedColumnIndex, setDraggedColumnIndex] = useState(null);
    const [dragEnabledIndex, setDragEnabledIndex] = useState(null);
    const draggedColumnIndexRef = useRef(null);
    const dragEnabledIndexRef = useRef(null);
    const columnsListRef = useRef(null);
    const autoScrollRafRef = useRef(null);
    const autoScrollSpeedRef = useRef(0);

    const { data: reportTableData, isLoading } = useGetReportTable(id);
    const updateReportMutation = useUpdateReport();

    const activeColorStyle = useMemo(
      () => getActiveColorStyle(activeColor),
      [activeColor]
    );

    // Theme detection
    useEffect(() => {
      const checkTheme = () => {
        setIsLightTheme(document.body.classList.contains("white-content"));
      };
      checkTheme();

      const observer = new MutationObserver(checkTheme);
      observer.observe(document.body, {
        attributes: true,
        attributeFilter: ["class"],
      });

      return () => observer.disconnect();
    }, []);

    // Dark mode detection
    useEffect(() => {
      const handleStorageChange = () => {
        const darkMode = localStorage.getItem("darkMode");
        const newTheme = darkMode ? JSON.parse(darkMode) : true;
        setCurrentTheme(newTheme);
      };

      window.addEventListener("storage", handleStorageChange);

      const interval = setInterval(() => {
        const darkMode = localStorage.getItem("darkMode");
        const newTheme = darkMode ? JSON.parse(darkMode) : true;
        if (newTheme !== currentTheme) {
          setCurrentTheme(newTheme);
        }
      }, 100);

      return () => {
        window.removeEventListener("storage", handleStorageChange);
        clearInterval(interval);
      };
    }, [currentTheme]);

    // Transform data structure for easier use
    const transformedData = useMemo(() => {
      if (!reportTableData?.data?.tables) return {};

      const tables = {};
      Object.keys(reportTableData.data.tables).forEach((tableName) => {
        tables[tableName] = reportTableData.data.tables[tableName].map(
          (fieldObj) => {
            const fieldKey = Object.keys(fieldObj)[0];
            const fieldData = fieldObj[fieldKey];
            return {
              id: fieldKey,
              label:
                fieldData.label ||
                (fieldKey
                  ? fieldKey
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (l) => l.toUpperCase())
                  : ""),
              type: fieldData.type || "text",
              value: fieldData.value || fieldKey,
              tableName: tableName,
            };
          }
        );
      });
      return tables;
    }, [reportTableData]);

    // Initialize selectedColumns with existing data
    useEffect(() => {
      if (data && data.length > 0) {
        const initializedColumns = data.map((existingCol) => {
          // Find the corresponding field data from transformed data
          const fieldData = Object.values(transformedData)
            .flat()
            .find((field) => field.id === existingCol.field_name);
          return {
            id: existingCol.id || existingCol.field_name, // Fallback to field_name to ensure unique ID
            label: existingCol.field_name,
            type: fieldData?.type || "text",
            value: fieldData?.value,
            tableName: existingCol.datasource_name || "unknown",
            customHeader: existingCol.header,
            existingId: existingCol.id, // Preserve the existing ID
          };
        });
        setSelectedColumns(initializedColumns);
      }
    }, [data, transformedData]);

    // Notify parent component when selectedColumns changes
    useEffect(() => {
      if (onColumnsChange) {
        const formattedColumns = selectedColumns.map((column) => ({
          field_name: column.id,
          header: column.customHeader || column.label,
          datasource_name: column.tableName,
        }));
        onColumnsChange(formattedColumns);
      }
    }, [selectedColumns, onColumnsChange]);

    // Save handler function
    const handleSave = async (options = {}) => {
      setIsSaving(true);
      try {
        const formattedColumns = selectedColumns.map((column) => {
          const columnData = {
            id: column.id,
            field_name: column.id, // Use column.id consistently for field_name
            header: column.customHeader || column.label,
            datasource_name: column.tableName,
          };

          // Always include id field - use existingId if available, otherwise use field_name as id
          if (column.existingId) {
            columnData.id = column.existingId;
          } else {
            columnData.id = column.id; // Use field_name as id for new columns
          }

          return columnData;
        });

        await updateReportMutation.mutateAsync({
          id: id,
          data: {
            columns: formattedColumns,
            permissons: permissions
          },
        });
      } catch (error) {
        console.error("Error saving columns:", error);
      } finally {
        setIsSaving(false);
      }
    };

    // Expose save method to parent component
    useImperativeHandle(ref, () => ({
      handleSave,
    }));

    // Filter fields based on search term and type filter
    const filteredFields = useMemo(() => {
      let filtered = transformedData;

      // Apply type filter
      if (activeFilter !== "all") {
        const typeMap = {
          text: "text",
          number: ["number", "id"],
          date: "date",
          currency: ["currency", "price"],
          phone: "phone",
          percentage: ["percentage", "percent"],
        };

        const typesToFilter = typeMap[activeFilter];
        if (typesToFilter) {
          const newFiltered = {};
          Object.keys(filtered).forEach((tableName) => {
            newFiltered[tableName] = filtered[tableName].filter((field) =>
              Array.isArray(typesToFilter)
                ? typesToFilter.includes(field.type)
                : field.type === typesToFilter
            );
          });
          filtered = newFiltered;
        }
      }

      // Apply search filter
      if (searchTerm) {
        const searchFiltered = {};
        Object.keys(filtered).forEach((tableName) => {
          searchFiltered[tableName] = filtered[tableName].filter(
            (field) =>
              field.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
              field.id.toLowerCase().includes(searchTerm.toLowerCase())
          );
        });
        filtered = searchFiltered;
      }

      return filtered;
    }, [transformedData, searchTerm, activeFilter]);

    useEffect(() => {
      if (searchTerm) {
        const newExpanded = {};
        Object.keys(filteredFields).forEach((tableName) => {
          if (filteredFields[tableName] && filteredFields[tableName].length > 0) {
            newExpanded[tableName] = true;
          }
        });
        setExpandedTables((prev) => ({ ...prev, ...newExpanded }));
      }
    }, [searchTerm, filteredFields]);

    const getFieldTypeIcon = (type) => {
      const baseColor = isLightTheme ? "#6c757d" : "#ffffff";
      const lightBg = isLightTheme ? "#e9ecef" : baseColor + "20";

      const iconMap = {
        text: { icon: "fas fa-font", color: baseColor, bg: lightBg },
        number: { icon: "fas fa-hashtag", color: baseColor, bg: lightBg },
        id: { icon: "fas fa-hashtag", color: baseColor, bg: lightBg },
        date: { icon: "fas fa-calendar", color: baseColor, bg: lightBg },
        currency: { icon: "fas fa-dollar-sign", color: baseColor, bg: lightBg },
        price: { icon: "fas fa-dollar-sign", color: baseColor, bg: lightBg },
        percentage: {
          icon: "fas fa-percent",
          color: baseColor,
          bg: lightBg,
        },
        percent: {
          icon: "fas fa-percent",
          color: baseColor,
          bg: lightBg,
        },
        phone: { icon: "fas fa-phone", color: baseColor, bg: lightBg },
      };

      const config = iconMap[type] || iconMap.text;
      return (
        <div
          className="d-flex align-items-center justify-content-center"
          style={{
            width: "24px",
            height: "24px",
            backgroundColor: config.bg,
            color: config.color,
            fontSize: "10px",
          }}
        >
          <i className={config.icon}></i>
        </div>
      );
    };

    const toggleTableExpansion = (tableName) => {
      setExpandedTables((prev) => ({
        ...prev,
        [tableName]: !prev[tableName],
      }));
    };

    const addColumn = (field) => {
      const existingColumn = selectedColumns.find((col) => col.id === field.id);

      if (existingColumn) {
        // If column already exists, remove it
        setSelectedColumns((prev) => prev.filter((col) => col.id !== field.id));
      } else {
        // Check if this column already exists in the data with an existing ID
        const existingDataColumn = data?.find(
          (col) => col.field_name === field.id
        );
        const columnToAdd = {
          ...field,
          // Set customHeader to formatted field name if not provided
          customHeader: field.label,
          // Preserve existing ID if this column was already saved
          ...(existingDataColumn?.id && { existingId: existingDataColumn.id }),
        };
        setSelectedColumns((prev) => [...prev, columnToAdd]);
      }
    };

    const removeColumn = (fieldId) => {
      setSelectedColumns((prev) => prev.filter((col) => col.id !== fieldId));
    };

    const updateColumnHeader = (fieldId, newHeader) => {
      setSelectedColumns((prev) =>
        prev.map((col) =>
          col.id === fieldId ? { ...col, customHeader: newHeader } : col
        )
      );
    };

    const stopAutoScroll = () => {
      autoScrollSpeedRef.current = 0;
      if (autoScrollRafRef.current != null) {
        cancelAnimationFrame(autoScrollRafRef.current);
        autoScrollRafRef.current = null;
      }
    };

    const getScrollParents = (el) => {
      const parents = [];
      let node = el;
      while (node && node !== document.body) {
        const style = window.getComputedStyle(node);
        const overflowY = style.overflowY;
        if (
          (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") &&
          node.scrollHeight > node.clientHeight
        ) {
          parents.push(node);
        }
        node = node.parentElement;
      }
      parents.push(document.scrollingElement || document.documentElement);
      return parents;
    };

    const runAutoScroll = () => {
      const speed = autoScrollSpeedRef.current;
      if (!speed) {
        autoScrollRafRef.current = null;
        return;
      }
      getScrollParents(columnsListRef.current).forEach((parent) => {
        parent.scrollTop += speed;
      });
      autoScrollRafRef.current = requestAnimationFrame(runAutoScroll);
    };

    const updateAutoScrollFromPointer = (clientY) => {
      const EDGE = 88;
      const vh = window.innerHeight;
      let speed = 0;
      if (clientY < EDGE) {
        speed = -Math.max(6, Math.ceil((EDGE - clientY) / 4));
      } else if (clientY > vh - EDGE) {
        speed = Math.max(6, Math.ceil((clientY - (vh - EDGE)) / 4));
      }
      autoScrollSpeedRef.current = speed;
      if (speed && autoScrollRafRef.current == null) {
        autoScrollRafRef.current = requestAnimationFrame(runAutoScroll);
      }
      if (!speed) {
        stopAutoScroll();
      }
    };

    const getInsertIndexFromPoint = (clientY) => {
      const list = columnsListRef.current;
      if (!list) return selectedColumns.length;
      const cards = list.querySelectorAll("[data-column-index]");
      for (let i = 0; i < cards.length; i += 1) {
        const rect = cards[i].getBoundingClientRect();
        const midY = rect.top + rect.height / 2;
        if (clientY < midY) {
          return Number(cards[i].getAttribute("data-column-index"));
        }
      }
      return cards.length;
    };

    useEffect(() => {
      const clearDragReady = () => {
        if (draggedColumnIndexRef.current === null) {
          setDragEnabledIndex(null);
          dragEnabledIndexRef.current = null;
        }
      };

      window.addEventListener("mouseup", clearDragReady);
      window.addEventListener("touchend", clearDragReady);
      window.addEventListener("touchcancel", clearDragReady);

      return () => {
        window.removeEventListener("mouseup", clearDragReady);
        window.removeEventListener("touchend", clearDragReady);
        window.removeEventListener("touchcancel", clearDragReady);
        stopAutoScroll();
      };
    }, []);

    const handleDragEnd = () => {
      stopAutoScroll();
      setDraggedColumnIndex(null);
      setDragEnabledIndex(null);
      draggedColumnIndexRef.current = null;
      dragEnabledIndexRef.current = null;
    };

    const handleDragStart = (event, index) => {
      if (dragEnabledIndexRef.current !== index) {
        setDragEnabledIndex(null);
        dragEnabledIndexRef.current = null;
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", String(index));
      setDraggedColumnIndex(index);
      draggedColumnIndexRef.current = index;
    };

    const reorderColumn = (originIndex, rawTargetIndex) => {
      if (
        originIndex === null ||
        originIndex < 0 ||
        rawTargetIndex == null ||
        rawTargetIndex < 0
      ) {
        handleDragEnd();
        return;
      }

      setSelectedColumns((prev) => {
        if (originIndex >= prev.length) return prev;
        let targetIndex = rawTargetIndex;
        if (targetIndex > originIndex) {
          targetIndex -= 1;
        }
        if (targetIndex === originIndex) return prev;
        const updated = [...prev];
        const [movedItem] = updated.splice(originIndex, 1);
        const boundedIndex = Math.max(0, Math.min(targetIndex, updated.length));
        updated.splice(boundedIndex, 0, movedItem);
        return updated;
      });

      handleDragEnd();
    };

    const handleListDragOver = (event) => {
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
      if (draggedColumnIndexRef.current !== null) {
        updateAutoScrollFromPointer(event.clientY);
      }
    };

    const handleListDrop = (event) => {
      event.preventDefault();
      event.stopPropagation();
      const originIndex = draggedColumnIndexRef.current;
      if (originIndex === null || originIndex < 0) {
        handleDragEnd();
        return;
      }
      const targetIndex = getInsertIndexFromPoint(event.clientY);
      reorderColumn(originIndex, targetIndex);
    };

    const handleDrop = (event, _targetIndex) => {
      event.preventDefault();
      event.stopPropagation();
      const originIndex = draggedColumnIndexRef.current;
      const targetIndex = getInsertIndexFromPoint(event.clientY);
      reorderColumn(originIndex, targetIndex);
    };

    const filterButtons = [
      { key: "all", label: "All", icon: "fas fa-th" },
      { key: "text", label: "Text", icon: "fas fa-font" },
      { key: "number", label: "Number", icon: "fas fa-hashtag" },
      { key: "date", label: "Date", icon: "fas fa-calendar" },
      { key: "currency", label: "Currency", icon: "fas fa-dollar-sign" },
      { key: "phone", label: "Phone", icon: "fas fa-phone" },
      { key: "percentage", label: "Percent", icon: "fas fa-percent", showPercent: false },
    ];

    const surface = useMemo(
      () => getReportSurfaceColors(isLightTheme, isTheme2),
      [isLightTheme, isTheme2]
    );

    if (isLoading) {
      return (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "400px" }}
        >
          <div className="text-center">
            <div
              className="spinner-border text-primary mb-3"
              role="status"
              style={{ width: "3rem", height: "3rem" }}
            >
              <span className="sr-only">Loading...</span>
            </div>
            <h5 className="text-light">Loading data sources...</h5>
          </div>
        </div>
      );
    }

    return (
      <div>
        <style jsx>{`
          .glass-card {
            border: 1px solid ${surface.border};
            box-shadow: ${isLightTheme
            ? "0 4px 6px rgba(0, 0, 0, 0.1)"
            : "0 4px 6px rgba(0, 0, 0, 0.3)"};
          }

          .modern-search {
            padding: 12px 20px;
            background: ${surface.panelBg};
            color: ${isLightTheme ? "#2d3748" : "white"};
            border: 1px solid ${surface.border};
          }

          .modern-search::placeholder {
            color: #a0aec0;
          }

          .filter-btn {
            background: ${surface.itemBg};
            border: 1px solid ${surface.border};
            padding: 6px 12px;
            margin: 2px;
            transition: all 0.3s ease;
            color: ${isLightTheme ? "#4a5568" : "white"};
          }

          .filter-btn.active {
            background: ${activeColorStyle.borderColor || "#007bff"};
            color: white;
          }

          .table-item {
            background: ${surface.itemBg};
            border: 1px solid ${surface.border};
            padding: 12px;
            margin-bottom: 8px;
            transition: all 0.3s ease;
            cursor: pointer;
          }

          .field-item {
            background: ${surface.itemBg};
            border: 1px solid ${surface.border};
            padding: 12px;
            margin-bottom: 8px;
            transition: all 0.3s ease;
            cursor: pointer;
          }

          .filter-card {
            background: ${surface.itemBg};
            border: 1px solid ${surface.border};
            margin-bottom: 12px;
            transition: all 0.3s ease;
            cursor: default;
          }

          .filter-card.dragging {
            opacity: 0.6;
            cursor: grabbing;
          }

          .filter-card.drag-ready {
            border-style: dashed;
            border-color: ${activeColorStyle.borderColor || "#007bff"};
            cursor: grab;
          }

          .drag-handle-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 32px;
            height: 32px;
            border-radius: 50%;
            border: none;
            background:transparent;
            color: ${isLightTheme ? "#4a5568" : "#ffffff"};
            cursor: grab;
            transition: all 0.2s ease;
            outline: none;
          }

          .drag-handle-btn.active,
          .drag-handle-btn:active {
            background: transparent;
            color: white;
            cursor: grabbing;
          }

          .drag-handle-btn i {
            font-size: 14px;
          }
          .table-item:hover {
            background: ${isLightTheme ? "#edf2f7" : "#5a6578"};
            transform: translateY(-1px);
            box-shadow: ${isLightTheme
            ? "0 2px 4px rgba(0, 0, 0, 0.1)"
            : "0 8px 25px rgba(0, 0, 0, 0.3)"};
          }

          .field-item {
            background: ${surface.itemBg};
            border: 1px solid ${surface.border};
            padding: 12px;
            margin-bottom: 8px;
            transition: all 0.3s ease;
            cursor: pointer;
          }

          .field-item:hover {
            background: ${isLightTheme ? "#edf2f7" : "#5a6578"};
            transform: translateY(-1px);
            box-shadow: ${isLightTheme
            ? "0 2px 4px rgba(0, 0, 0, 0.1)"
            : "0 8px 25px rgba(0, 0, 0, 0.3)"};
          }

          .field-item.selected {
            background: transparent;
            color: white;
          }

          .empty-state {
            border: 2px dashed ${activeColorStyle.borderColor || "#007bff"}50;
            padding: 3rem;
            text-align: center;
            color: #6b7280;
          }

          .section-title {
            color: ${activeColorStyle.color || "#007bff"};
            font-weight: 600;
            font-size: 1rem;
            margin-bottom: 1rem;
          }
          .selected-filters-container {
            padding: 20px;
            box-shadow: ${isLightTheme
            ? "0 2px 4px rgba(0, 0, 0, 0.1)"
            : "0 4px 6px rgba(0, 0, 0, 0.3)"};
          }
        `}</style>

        <div>
          <Row>
            {/* Selected Columns Panel */}
            <Col md={closeSourceArea ? 12 : 6}>
              <Card className="selected-filters-container p-3" >
                <CardTitle
                  tag="h5"
                  className={isLightTheme ? "text-dark" : "text-white"}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    Selected Columns
                    {closeSourceArea && (
                      <Button
                        onClick={() => setCloseSourceArea(!closeSourceArea)}
                        color="link"
                        className="px-2 py-0"
                        style={{ color: isLightTheme ? "black" : "white" }}
                      >
                        <i className="tim-icons icon-simple-add"></i>
                      </Button>
                    )}
                  </div>
                </CardTitle>

                {selectedColumns.length === 0 ? (
                  <div className="text-center text-muted py-5">
                    <div className="mb-3">
                      <i className="fas fa-table fa-3x text-light"></i>
                    </div>
                    <h6
                      className={isLightTheme ? "text-dark" : "text-white"}
                      style={{ marginBottom: "0.5rem" }}
                    >
                      No columns selected
                    </h6>
                    <small
                      className={isLightTheme ? "text-dark" : "text-white"}
                    >
                      Choose fields from the data sources panel to build your
                      report
                    </small>
                  </div>
                ) : (
                  <div
                    ref={columnsListRef}
                    onDragOver={handleListDragOver}
                    onDrop={handleListDrop}
                    onDragLeave={(event) => {
                      if (!columnsListRef.current?.contains(event.relatedTarget)) {
                        stopAutoScroll();
                      }
                    }}
                  >
                    {selectedColumns.map((field, index) => (
                      <div
                        key={field.id}
                        data-column-index={index}
                        className={`filter-card${draggedColumnIndex === index ? " dragging" : ""
                          }${dragEnabledIndex === index ? " drag-ready" : ""
                          }`}
                        style={{
                          marginBottom: "5px",
                          paddingRight: "10px",
                          padding: "5px",
                        }}
                        draggable
                        onDragStart={(event) => handleDragStart(event, index)}
                        onDragOver={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          event.dataTransfer.dropEffect = "move";
                          updateAutoScrollFromPointer(event.clientY);
                        }}
                        onDrop={(event) => handleDrop(event, index)}
                        onDragEnd={handleDragEnd}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center flex-grow-1">
                            <div className="mr-3">
                              <button
                                type="button"
                                className={`drag-handle-btn ${dragEnabledIndex === index ? "active" : ""
                                  }`}
                                onMouseDown={() => {
                                  setDragEnabledIndex(index);
                                  dragEnabledIndexRef.current = index;
                                }}
                                onMouseUp={() => {
                                  if (draggedColumnIndex === null) {
                                    setDragEnabledIndex(null);
                                    dragEnabledIndexRef.current = null;
                                  }
                                }}
                                onTouchStart={() => {
                                  setDragEnabledIndex(index);
                                  dragEnabledIndexRef.current = index;
                                }}
                                onTouchEnd={() => {
                                  if (draggedColumnIndex === null) {
                                    setDragEnabledIndex(null);
                                    dragEnabledIndexRef.current = null;
                                  }
                                }}
                                onTouchCancel={() => {
                                  if (draggedColumnIndex === null) {
                                    setDragEnabledIndex(null);
                                    dragEnabledIndexRef.current = null;
                                  }
                                }}
                                onMouseLeave={(event) => {
                                  if (
                                    dragEnabledIndex === index &&
                                    draggedColumnIndex === null &&
                                    event.buttons === 0
                                  ) {
                                    setDragEnabledIndex(null);
                                    dragEnabledIndexRef.current = null;
                                  }
                                }}
                              >
                                <MdOutlineDragIndicator size={40} color={`${dragEnabledIndex === index ? "#007bff" : isLightTheme ? "#4a5568" : "#ffffff"}`} />
                              </button>
                            </div>
                            <div className="mr-3">
                              {getFieldTypeIcon(field.type)}
                            </div>
                            <div className="d-flex flex-column">
                              <span
                                className={`mb-0 pb-0 ${isLightTheme ? "text-dark" : "text-white"
                                  }`}
                              >
                                {field.label && field.label
                                  .replace(/_/g, " ")
                                  .replace(/\b\w/g, (l) => l.toUpperCase())}
                              </span>
                              <span
                                className={`mt-0 pt-0 ${isLightTheme ? "text-dark" : "text-white"
                                  }`}
                                style={{ fontSize: "0.8rem", opacity: 0.5 }}
                              >
                                {field.tableName ? field.tableName.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()) : ''}
                              </span>
                            </div>
                          </div>
                          <div className="d-flex align-items-center">
                            <Input
                              type="text"
                              value={field.customHeader}
                              onChange={(e) =>
                                updateColumnHeader(field.id, e.target.value)
                              }
                              style={{
                                minWidth: "250px",
                                fontSize: "0.9rem",
                                height: "30px",
                                fontSize: "0.8rem",
                              }}
                              placeholder="Column header"
                            />
                            <Button
                              color="link"
                              onClick={() => removeColumn(field.id)}
                              className="ml-2 p-0"
                              style={{
                                transition: 'all 0.3s ease',
                                transform: 'scale(1)',
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.transform = 'scale(1.1)';
                                e.target.style.color = '#dc3545';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.transform = 'scale(1)';
                                e.target.style.color = '';
                              }}
                            >
                              <i
                                style={{
                                  height: "16px",
                                  width: "16px",
                                  fontSize: "12px"
                                }}
                                className="fas fa-trash text-light"
                              ></i>
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </Col>

            {/* Available Data Sources Panel */}
            <Col md={6} className={closeSourceArea ? "d-none" : ""}>
              <Card
                className="glass-card p-3"
                style={{ maxHeight: "80vh", overflowY: "auto", paddingTop: "10px !important" }}
              >
                <CardTitle
                  tag="h5"
                  className={`mb-2 ${isLightTheme ? "text-dark" : "text-white"}`}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                      <i className="fas fa-database mr-2"></i>
                      Data Sources
                    </div>
                    {!closeSourceArea && (
                      <Button
                        color="link"
                        className="px-2 py-0"
                        style={{ color: isLightTheme ? "black" : "white" }}
                        onClick={() => setCloseSourceArea(!closeSourceArea)}
                      >
                        <i className="tim-icons icon-simple-remove" />
                      </Button>
                    )}
                  </div>
                </CardTitle>

                <div className=" d-flex justify-content-between align-items-center">
                  <div className="mb-2">
                    {filterButtons.map((filter) => (
                      <button
                        key={filter.key}
                        className={`filter-btn ${activeFilter === filter.key ? "active" : ""
                          }`}
                        onClick={() => setActiveFilter(filter.key)}
                      >
                        <i className={`${filter.icon} `} style={{ fontSize: filter.key === "percentage" ? "12px" : "14px" }}></i>
                        {filter.showPercent && <span style={{ fontSize: "12px", marginLeft: "2px", fontWeight: "bold" }}>%</span>}
                      </button>
                    ))}
                  </div>
                  <div className="position-relative mb-2">
                    <input
                      type="text"
                      className="form-control modern-search"
                      placeholder="Search fields..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{ color: isLightTheme ? "#2d3748" : "white" }}
                    />
                    <div
                      className="position-absolute"
                      style={{
                        right: "15px",
                        top: "50%",
                        transform: "translateY(-50%)",
                      }}
                    >
                      <i
                        className={`fas fa-search ${isLightTheme ? "text-dark" : "text-light"
                          }`}
                      ></i>
                    </div>
                  </div>
                </div>

                <div style={{ maxHeight: "65vh", overflowY: "auto" }}>
                  {Object.keys(filteredFields).map((tableName) => (
                    <div key={tableName} className="mb-0">
                      <div
                        className="table-item d-flex align-items-center justify-content-between"
                        onClick={() => toggleTableExpansion(tableName)}
                      >
                        <div className="d-flex align-items-center">
                          <i
                            style={{ color: activeColorStyle.borderColor }}
                            className="fas fa-table mr-2"
                          ></i>
                          <h6
                            className={`mb-0 text-capitalize ${isLightTheme ? "text-dark" : "text-white"
                              }`}
                          >
                            {tableName ? tableName.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()) : ''}{" "}
                            <small style={{ opacity: 0.5 }}>
                              {filteredFields[tableName].length} fields
                            </small>
                          </h6>
                        </div>
                        <i
                          className={`fas fa-chevron-${expandedTables[tableName] ? "up" : "down"
                            } ${isLightTheme ? "text-dark" : "text-white"}`}
                        />
                      </div>

                      {expandedTables[tableName] && (
                        <div className="ml-3 mt-2">
                          {filteredFields[tableName].map((field) => (
                            <div
                              key={field.id}
                              className={`field-item p-1 ${selectedColumns.find(
                                (col) => col.id === field.id
                              )
                                ? "selected"
                                : ""
                                }`}
                              onClick={() => addColumn(field)}
                            >
                              <div className="d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center">
                                  <div className="mr-3">
                                    {getFieldTypeIcon(field.type)}
                                  </div>
                                  <div>
                                    <span
                                      style={{ fontSize: 12 }}
                                      className={`mb-0  ${isLightTheme
                                        ? selectedColumns.find(
                                          (col) => col.id === field.id
                                        ) ? "text-white" : "text-dark"
                                        : "text-white"
                                        }`}
                                    >
                                      {field.label}
                                    </span>
                                  </div>
                                </div>
                                {selectedColumns.find(
                                  (col) => col.id === field.id
                                ) && (
                                    <div
                                      className="d-flex align-items-center justify-content-center"
                                      style={{
                                        width: "20px",
                                        height: "20px",
                                        background: "rgba(255, 255, 255, 0.2)",
                                        color: "white",
                                      }}
                                    >
                                      <i className="fas fa-check"></i>
                                    </div>
                                  )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    );
  }
);

ReportColumns.displayName = "ReportColumns";

export default ReportColumns;
